import { useState } from 'react';

import {
  AppBar,
  Avatar,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Link,
  ListItem,
  ListItemAvatar,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import { Search } from '@material-ui/icons';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DoneIcon from '@material-ui/icons/CheckCircleOutline';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useSnackbar } from 'notistack';
import { useModal } from 'react-modal-hook';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import ButtonCancelarTransferencia from 'components/ButtonCancelarTransferencia';
import ModalFinalizarAtendimento from 'components/ModalFinalizarAtendimento';
import ModalTransferirAtendimento from 'components/ModalTransferirAtendimento';
import OfficialWhatsAppIndicator from 'components/OfficialWhatsAppIndicator';
import DrawerDetalhesContato from 'pages/Contatos/components/DrawerDetalhesContato';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atendimentosSelector,
  finalizarAtendimento,
  setAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import { TipoCanalEnum } from 'store/reducers/canais';

import CronometroSessaoAtendimento from './CronometroSessaoAtendimento';
// import DrawerDetalhesAtendimento from './DrawerDetalhesAtendimento';

const useStyles = makeStyles(({ palette, spacing, shape }) => ({
  root: {
    display: 'flex',
    overflow: 'hidden',
    borderRadius: shape.borderRadius,
    backgroundColor: palette.background.paper,
    color: palette.text.primary,
    marginBottom: spacing(1),
  },
  buttonDetalhesAtendimento: {
    fontSize: 12,
    fontWeight: 500,

    border: `1px solid ${palette.primary.main}`,
    borderRadius: 20,
    color: palette.primary.main,

    '&:hover': {
      backgroundColor: alpha(palette.primary.main, 0.1),
    },
  },
}));

interface TopContentAppBarProps {
  onPesquisarMensagens: () => void;
}

export default function TopContentAppBar({
  onPesquisarMensagens,
}: TopContentAppBarProps) {
  const classes = useStyles();
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const isMedium = useMediaQuery('(min-width:1100px)');

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { atendimentoSelecionado, loadingFinalizarAtendimento } =
    useAppSelector(atendimentosSelector);

  const [tabIndexDrawerDetalhesContato, setTabIndexDrawerDetalhesContato] =
    useState<
      'contacts' | 'activities' | 'files' | 'attendance' | 'history' | 'funnel'
    >('attendance');

  const tipoCanal = atendimentoSelecionado?.canal?.tipoCanal;

  const isOfficialWhatsAppChannel =
    tipoCanal === TipoCanalEnum.WHATSAPP_OFICIAL ||
    (atendimentoSelecionado?.canal?.isModoHibrido &&
      atendimentoSelecionado?.canal?.modoEnvio === 'API_OFICIAL');

  const isInstagramOrMessengerChannel =
    tipoCanal === TipoCanalEnum.INSTAGRAM ||
    tipoCanal === TipoCanalEnum.MESSENGER;

  const handleBack = () => {
    dispatch(setAtendimentoSelecionado());
  };

  const isBlocked = !!(
    atendimentoSelecionado?.contato.blockedAt ||
    atendimentoSelecionado?.contato?.bloqueadoEm
  );

  const [showModalFinalizar, hideModalFinalizar] = useModal(
    ({ in: open }) => (
      <ModalFinalizarAtendimento
        open={open}
        onClose={hideModalFinalizar}
        contactName={atendimentoSelecionado?.contato.nome ?? ''}
        loading={loadingFinalizarAtendimento}
        onConfirm={data => {
          if (atendimentoSelecionado) {
            dispatch(
              finalizarAtendimento({
                atendimentoId: atendimentoSelecionado.id,
                ...data,
              }),
            )
              .unwrap()
              .then(() => {
                enqueueSnackbar('O atendimento foi finalizado com sucesso', {
                  variant: 'success',
                });
                dispatch(setAtendimentoSelecionado());
                hideModalFinalizar();
              })
              .catch(error => {
                if (error.response)
                  enqueueSnackbar(error.response.data.message);
              });
          }
        }}
      />
    ),
    [atendimentoSelecionado, loadingFinalizarAtendimento],
  );

  const handleConfirmFinalizar = () => {
    if (atendimentoSelecionado) {
      showModalFinalizar();
    }
  };

  const [showDrawerAtendimento, hideDrawerAtendimento] = useModal(
    ({ in: open }) => {
      if (!atendimentoSelecionado) return null;

      return (
        // <DrawerDetalhesAtendimento
        //   open={open}
        //   onClose={hideDrawerAtendimento}
        //   atendimento={atendimentoSelecionado}
        // />
        <DrawerDetalhesContato
          open={open}
          onClose={hideDrawerAtendimento}
          contatoId={atendimentoSelecionado?.contato.id}
          atendimento={atendimentoSelecionado}
          tabIndex={tabIndexDrawerDetalhesContato}
        />
      );
    },
    [atendimentoSelecionado, tabIndexDrawerDetalhesContato],
  );

  const [showModalTransferirAtendimento, hideModalTransferirAtendimento] =
    useModal(
      ({ in: open }) => (
        <ModalTransferirAtendimento
          open={open}
          onClose={hideModalTransferirAtendimento}
          atendimento={atendimentoSelecionado}
        />
      ),
      [atendimentoSelecionado],
    );

  const handleOpenDrawerDetalhesContato = (tabIndex: number) => {
    const tabKeyMap: Record<
      number,
      'contacts' | 'activities' | 'files' | 'attendance' | 'history' | 'funnel'
    > = {
      0: 'contacts',
      3: 'attendance',
    };
    setTabIndexDrawerDetalhesContato(tabKeyMap[tabIndex]);
    showDrawerAtendimento();
  };

  return (
    <AppBar className={classes.root} position="relative" elevation={0}>
      <ListItem dense>
        <Box mr={2}>
          <ButtonBase onClick={handleBack} disableRipple>
            <ArrowBackIcon />
          </ButtonBase>
        </Box>

        <ListItemAvatar style={{ minWidth: 40, paddingRight: 8 }}>
          <ButtonBase
            onClick={() => handleOpenDrawerDetalhesContato(0)}
            disableRipple
          >
            <BlockedContactBadge isBlocked={isBlocked}>
              <Avatar src={atendimentoSelecionado?.contato.foto} />
            </BlockedContactBadge>
          </ButtonBase>
        </ListItemAvatar>

        <Box display="flex" alignItems="baseline">
          <Box display="flex" flexDirection="column">
            <Box
              onClick={() => handleOpenDrawerDetalhesContato(0)}
              component={Link}
              color="inherit"
              title={`Editar ${atendimentoSelecionado?.contato?.nome}`}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <BlockedContactName
                name={atendimentoSelecionado?.contato.nome ?? ''}
                isBlocked={isBlocked}
                variant="body2"
                style={{ maxWidth: '24ch' }}
              />
            </Box>

            {isOfficialWhatsAppChannel &&
              atendimentoSelecionado?.sessaoStatus && (
                <OfficialWhatsAppIndicator size="small" />
              )}
          </Box>
        </Box>

        <Box flexGrow={1} />

        {(isOfficialWhatsAppChannel || isInstagramOrMessengerChannel) &&
          atendimentoSelecionado?.sessaoStatus &&
          isMedium && (
            <Box mr={2}>
              <CronometroSessaoAtendimento
                atendimento={atendimentoSelecionado}
              />
            </Box>
          )}

        <Box flexGrow={1} />

        {atendimentoSelecionado && isLarge && (
          <Box mr={2}>
            <Button
              variant="outlined"
              className={classes.buttonDetalhesAtendimento}
              onClick={() => handleOpenDrawerDetalhesContato(3)}
              size="small"
            >
              Detalhes do atendimento
            </Button>
          </Box>
        )}

        {atendimentoSelecionado?.transferenciaPendenteId && (
          <Box mr={1}>
            <ButtonCancelarTransferencia
              transferenciaPendenteId={
                atendimentoSelecionado.transferenciaPendenteId
              }
            />
          </Box>
        )}

        {!atendimentoSelecionado?.transferenciaPendenteId && (
          <Box mr={1}>
            <Tooltip title="Transferir">
              <IconButton onClick={showModalTransferirAtendimento}>
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <Tooltip title="Pesquisar mensagem">
          <IconButton onClick={onPesquisarMensagens}>
            <Search />
          </IconButton>
        </Tooltip>

        <Tooltip title="Finalizar">
          <IconButton
            onClick={handleConfirmFinalizar}
            disabled={loadingFinalizarAtendimento}
          >
            <DoneIcon />
          </IconButton>
        </Tooltip>
      </ListItem>
    </AppBar>
  );
}
